import type { EnvironmentProviders, Provider } from '@angular/core';
import { campaignIdsWithExpirationDate } from '@bcf-v2-configurators/tokens/affiliate/lvbetlv/affiliate-ids-with-expiration-date';
import { CAMPAIGN_IDS_WITH_EXPIRATION_DATE } from '@bcf-v2-configurators/tokens/affiliate/token';
import { applePayByPiqPaymentMethodEnabled } from '@bcf-v2-configurators/tokens/applepay/lvbetlv/applepay-by-piq-enabled';
import { APPLEPAY_BY_PIQ_PAYMENT_METHOD_ENABLED } from '@bcf-v2-configurators/tokens/applepay/token';
import { bonusesEnabled } from '@bcf-v2-configurators/tokens/bonuses/lvbetlv/bonuses-enabled';
import { BONUSES_ENABLED } from '@bcf-v2-configurators/tokens/bonuses/token';
import { gamesImagesAssetsUrl } from '@bcf-v2-configurators/tokens/casino/lvbetlv/games-images-assets-url';
import { slideTermsEnabled } from '@bcf-v2-configurators/tokens/casino/lvbetlv/slide-terms-enabled';
import { GAMES_IMAGES_ASSETS_URL, SLIDE_TERMS_ENABLED } from '@bcf-v2-configurators/tokens/casino/token';
import { COOKIE_CONSENT_GAMBLING_ADULT_LOADER } from '@bcf-v2-configurators/tokens/cookie-consent/token';
import { USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE } from '@bcf-v2-configurators/tokens/date-format/token';
import { defaultLang } from '@bcf-v2-configurators/tokens/default-lang/lvbetlv/default-lang';
import { DEFAULT_LANG } from '@bcf-v2-configurators/tokens/default-lang/token';
import { I18N_OPTIMOVE_DESC_LOADER } from '@bcf-v2-configurators/tokens/descs-optimove-i18n/token';
import { I18N_DESC_LOADER } from '@bcf-v2-configurators/tokens/descs/token';
import { editProfileAddressAvailableFieldsFn } from '@bcf-v2-configurators/tokens/edit-profile/lvbetcom/edit-profile-available-fields';
import { editProfileShowBankAccountInfo } from '@bcf-v2-configurators/tokens/edit-profile/lvbetpl/edit-profile-show-bank-account-info';
import {
  EDIT_PROFILE_ADDRESS_AVAILABLE_FIELDS,
  EDIT_PROFILE_SHOW_BANK_ACCOUNT_INFO
} from '@bcf-v2-configurators/tokens/edit-profile/token';
import { footerLinksFn } from '@bcf-v2-configurators/tokens/footer/lvbetlv/casino-footer-links';
import { spSidebarLinksFn } from '@bcf-v2-configurators/tokens/footer/lvbetlv/casino-sp-sidebar-links';
import { footerBrandName } from '@bcf-v2-configurators/tokens/footer/lvbetlv/footer-brand-name';
import { FOOTER_BRAND_NAME, FOOTER_LINKS, SP_SIDEBAR_LINKS } from '@bcf-v2-configurators/tokens/footer/token';
import { imgWebConfig } from '@bcf-v2-configurators/tokens/img-webp/lvbetlv/img-web-config';
import { IMG_WEB_CONFIG } from '@bcf-v2-configurators/tokens/img-webp/token';
import { introHasCasinoComponents } from '@bcf-v2-configurators/tokens/intro/lvbetlv/intro-has-casino-components';
import { INTRO_HAS_CASINO_COMPONENTS } from '@bcf-v2-configurators/tokens/intro/token';
import { ladeskChatAdditionalConfig } from '@bcf-v2-configurators/tokens/ladesk-chat/lvbetlv/ladesk-chat-additional-config';
import { LADESK_CHAT_ADDITIONAL_CONFIG } from '@bcf-v2-configurators/tokens/ladesk-chat/token';
import { lvbetcomLanguageSwitcherFlagMap } from '@bcf-v2-configurators/tokens/language-switcher/lvbetcom/lvbetcom-language-switcher-flag-map';
import { LANGUAGE_SWITCHER_FLAG_MAP } from '@bcf-v2-configurators/tokens/language-switcher/token';
import { lotteryBrandName } from '@bcf-v2-configurators/tokens/lottery/lvbet/lottery-brand-name';
import { LOTTERY_BRAND_NAME } from '@bcf-v2-configurators/tokens/lottery/token';
import { lvWheelConfig } from '@bcf-v2-configurators/tokens/lv-wheel/lvbetlv/lv-wheel-config';
import { lotteryAssetsUrlValue } from '@bcf-v2-configurators/tokens/lv-wheel/lvbetpl/lottery-assets-url';
import { LV_WHEEL_CONFIG } from '@bcf-v2-configurators/tokens/lv-wheel/token';
import { mobileApplicationAppConfig } from '@bcf-v2-configurators/tokens/mobile-application/casino/lvbetlv/config';
import { MOBILE_APPLICATION_APPS_CONFIG } from '@bcf-v2-configurators/tokens/mobile-application/token';
import { modalCookiesShowIndicator } from '@bcf-v2-configurators/tokens/modals/lvbetcom/modal-cookies-show-indicator';
import { modalCookiesConsentMode } from '@bcf-v2-configurators/tokens/modals/lvbetlv/modal-cookies-consent-mode';
import { modalNewTermsMode } from '@bcf-v2-configurators/tokens/modals/lvbetlv/modal-new-terms-mode';
import {
  MODAL_COOKIES_CONSENT_MODE,
  MODAL_COOKIES_INDICATOR,
  MODAL_NEW_TERMS_MODE
} from '@bcf-v2-configurators/tokens/modals/token';
import { OPTIMOVE_SDK_CONFIG_LOADER } from '@bcf-v2-configurators/tokens/optimove-sdk/tokens';
import { piqStyles } from '@bcf-v2-configurators/tokens/piq-styles/lvbetlv/piq-styles';
import { piqTheme } from '@bcf-v2-configurators/tokens/piq-styles/lvbetlv/piq-theme';
import { PIQ_CSS, PIQ_THEME } from '@bcf-v2-configurators/tokens/piq-styles/token';
import { PUSH_NOTIFICATIONS_CONFIG_LOADER } from '@bcf-v2-configurators/tokens/push-notifications/token';
import { rgLimitsNonParallelPredefinedAmounts } from '@bcf-v2-configurators/tokens/rg-limits-parallel/lvbetlv/rg-limits-non-parallel-predefined-amounts';
import { RG_LIMITS_NON_PARALLEL_PREDEFINED_AMOUNTS } from '@bcf-v2-configurators/tokens/rg-limits-parallel/token';
import { LOGIN_SMART_ID_ENABLED } from '@bcf-v2-configurators/tokens/smart-id/token';
import { sportsLinkMobileWeb } from '@bcf-v2-configurators/tokens/sports-links/lvbetlv/sports-links';
import { SPORTS_FULL_URL } from '@bcf-v2-configurators/tokens/sports-links/token';
import { provideVanillaTsTokens } from '@bcf-v2-shared/misc/vanilla-ts-providers/provide-vanilla-ts-tokens';
import { logRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/lvbetlv/log-rocket-api-key';
import { registerLogRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/token';
import { LOTTERY_ASSETS_URL } from '@bcf-vanilla-ts-v1-configurators/tokens/lottery/token';
import { cpSortedProviderNamesToOrder } from '@bcf-vanilla-ts-v1-configurators/tokens/slots/lvbetlv/cp-sorted-provider-ids-to-order';
import { SORTED_PROVIDERS_IDS_TO_ORDER } from '@bcf-vanilla-ts-v1-configurators/tokens/slots/token';

export const settingsProviders: (Provider | EnvironmentProviders)[] = [
  {
    provide: IMG_WEB_CONFIG,
    useValue: imgWebConfig
  },
  {
    provide: DEFAULT_LANG,
    useValue: defaultLang
  },
  {
    provide: LV_WHEEL_CONFIG,
    useValue: lvWheelConfig
  },
  {
    provide: LOGIN_SMART_ID_ENABLED,
    useValue: true
  },
  {
    provide: I18N_DESC_LOADER,
    useValue: () =>
      import(
        /* webpackChunkName: "const-i18n-desc" */ '@bcf-v2-configurators/tokens/descs/lvbetlv/mobile-web/i18n-descs'
        // eslint-disable-next-line @typescript-eslint/typedef
      ).then((mod) => mod.i18nDesc)
  },
  {
    provide: GAMES_IMAGES_ASSETS_URL,
    useValue: gamesImagesAssetsUrl
  },
  {
    provide: SLIDE_TERMS_ENABLED,
    useValue: slideTermsEnabled
  },
  {
    provide: EDIT_PROFILE_ADDRESS_AVAILABLE_FIELDS,
    useValue: editProfileAddressAvailableFieldsFn
  },
  {
    provide: EDIT_PROFILE_SHOW_BANK_ACCOUNT_INFO,
    useValue: editProfileShowBankAccountInfo
  },
  {
    provide: LADESK_CHAT_ADDITIONAL_CONFIG,
    useValue: ladeskChatAdditionalConfig
  },
  {
    provide: LANGUAGE_SWITCHER_FLAG_MAP,
    useValue: lvbetcomLanguageSwitcherFlagMap
  },
  {
    provide: SPORTS_FULL_URL,
    useValue: sportsLinkMobileWeb
  },
  {
    provide: BONUSES_ENABLED,
    useValue: bonusesEnabled
  },
  {
    provide: FOOTER_LINKS,
    useValue: footerLinksFn
  },
  {
    provide: SP_SIDEBAR_LINKS,
    useValue: spSidebarLinksFn
  },
  {
    provide: FOOTER_BRAND_NAME,
    useValue: footerBrandName
  },
  {
    provide: MOBILE_APPLICATION_APPS_CONFIG,
    useValue: mobileApplicationAppConfig
  },
  {
    provide: PUSH_NOTIFICATIONS_CONFIG_LOADER,
    useValue: () =>
      import('@bcf-v2-configurators/tokens/push-notifications/lvbetlv/push-notifications-config').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.pushNotificationsConfigs
      )
  },
  {
    provide: OPTIMOVE_SDK_CONFIG_LOADER,
    useValue: () =>
      import('@bcf-v2-configurators/tokens/optimove-sdk/lvbetlv/casino/mobile-web/optimove-sdk-config').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.optimoveSdkConfigs
      )
  },
  {
    provide: I18N_OPTIMOVE_DESC_LOADER,
    useValue: () =>
      import('@bcf-v2-configurators/tokens/descs-optimove-i18n/lvbetlv/casino/i18n-optimove-desc').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.i18nOptimoveDesc
      )
  },
  {
    provide: LOTTERY_ASSETS_URL,
    useValue: lotteryAssetsUrlValue
  },
  {
    provide: RG_LIMITS_NON_PARALLEL_PREDEFINED_AMOUNTS,
    useValue: rgLimitsNonParallelPredefinedAmounts
  },
  {
    provide: USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE,
    useValue: true
  },
  {
    provide: CAMPAIGN_IDS_WITH_EXPIRATION_DATE,
    useValue: campaignIdsWithExpirationDate
  },
  {
    provide: MODAL_NEW_TERMS_MODE,
    useValue: modalNewTermsMode
  },
  {
    provide: MODAL_COOKIES_INDICATOR,
    useValue: modalCookiesShowIndicator
  },
  {
    provide: MODAL_COOKIES_CONSENT_MODE,
    useValue: modalCookiesConsentMode
  },
  {
    provide: COOKIE_CONSENT_GAMBLING_ADULT_LOADER,
    useValue: () =>
      import(
        /* webpackChunkName: "const-cookie-consent-gambling-adult" */ '@bcf-v2-configurators/tokens/cookie-consent/projects/sportsbook/lvbetlv/cookie-consent-gambling-adult'
        // eslint-disable-next-line @typescript-eslint/typedef
      ).then((mod) => mod.cookieConsentGamblingAdult)
  },
  { provide: PIQ_THEME, useValue: piqTheme },
  { provide: PIQ_CSS, useValue: piqStyles },
  {
    provide: LOTTERY_BRAND_NAME,
    useValue: lotteryBrandName
  },
  {
    provide: SORTED_PROVIDERS_IDS_TO_ORDER,
    useValue: cpSortedProviderNamesToOrder
  },
  provideVanillaTsTokens(() => {
    registerLogRocketApiKey(logRocketApiKey);
  }),
  {
    provide: INTRO_HAS_CASINO_COMPONENTS,
    useValue: introHasCasinoComponents
  },
  {
    provide: APPLEPAY_BY_PIQ_PAYMENT_METHOD_ENABLED,
    useValue: applePayByPiqPaymentMethodEnabled
  }
];
